import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import ManageAdminsModal from "./ManageAdminsModal";
import "./AdminUserSelector.css";

function AdminUserSelectorView({
    loading,
    availableUsers,
    authUser,
    setAuthUser,
    onSelectUser,
    onSignOut,
    
    showManageAdmins,
    setShowManageAdmins,
    adminsList,
    newAdminEmail,
    setNewAdminEmail,
    handleAddAdmin,
    handleRemoveAdmin,
    saneEmail
}) {
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Support matching both raw input and dot-replaced sanitized ID
    const sanitizedAuthUser = authUser ? authUser.replace(/\./g, "-") : "";
    const isViewEnabled = sanitizedAuthUser && (
        availableUsers.includes(authUser) ||
        availableUsers.includes(sanitizedAuthUser)
    );

    // Reconstructs the email format (e.g. replacing hyphens in the domain part back to dots)
    const toDisplayEmail = (userId) => {
        if (!userId) return "";
        if (userId.includes("@")) {
            const [username, domain] = userId.split("@");
            return `${username}@${domain.replace(/-/g, ".")}`;
        }
        return userId;
    };

    // Helper to check if a user item matches the search term
    const matchesSearch = (user, searchVal) => {
        if (!searchVal || searchVal.trim() === "") return false;
        const lowerSearch = searchVal.trim().toLowerCase();
        const lowerUser = user.toLowerCase();
        const lowerEmail = toDisplayEmail(user).toLowerCase();
        return lowerUser.startsWith(lowerSearch) || lowerEmail.startsWith(lowerSearch);
    };

    // Filter availableUsers based on authUser query
    const filteredUsers = availableUsers.filter(user => matchesSearch(user, authUser));

    return (
        <div className="admin-user-selector">
            <button
                className="aus-manage-btn"
                onClick={() => setShowManageAdmins(true)}
            >
                Manage Admins
            </button>

            <h2>Admin - Select User</h2>

            {loading && <div className="aus-loading">Loading users...</div>}

            {!loading && availableUsers.length === 0 && (
                <div className="aus-no-users">No users found in database.</div>
            )}

            {!loading && availableUsers.length > 0 && (
                <>
                    <div className="aus-field">
                        <label htmlFor="user-search">Select User ID:</label>
                        <input
                            id="user-search"
                            type="text"
                            className="aus-input"
                            placeholder="Type to search..."
                            value={authUser}
                            onChange={(e) => {
                                setAuthUser(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                            autoComplete="off"
                        />
                        {showSuggestions && authUser && authUser.trim() !== "" && filteredUsers.length > 0 && (
                            <ul className="aus-suggestions-list">
                                {filteredUsers.map(user => {
                                    const email = toDisplayEmail(user);
                                    return (
                                        <li
                                            key={user}
                                            className="aus-suggestion-item"
                                            onClick={() => {
                                                setAuthUser(email);
                                                setShowSuggestions(false);
                                            }}
                                        >
                                            <div className="aus-suggestion-email">{email}</div>
                                            {email !== user && (
                                                <div className="aus-suggestion-id">{user}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="aus-actions">
                        <button 
                            className="aus-button-primary" 
                            onClick={onSelectUser}
                            disabled={!isViewEnabled}
                        >
                            View User Data
                        </button>
                        <button className="aus-button-secondary" onClick={onSignOut}>
                            Sign Out
                        </button>
                    </div>
                </>
            )}

            {showManageAdmins && (
                <ManageAdminsModal
                    onClose={() => setShowManageAdmins(false)}
                    newAdminEmail={newAdminEmail}
                    setNewAdminEmail={setNewAdminEmail}
                    onAddAdmin={handleAddAdmin}
                    adminsList={adminsList}
                    onRemoveAdmin={handleRemoveAdmin}
                    saneEmail={saneEmail}
                />
            )}
        </div>
    );
}

AdminUserSelectorView.propTypes = {
    loading: PropTypes.bool.isRequired,
    availableUsers: PropTypes.arrayOf(PropTypes.string).isRequired,
    authUser: PropTypes.string.isRequired,
    setAuthUser: PropTypes.func.isRequired,
    onSelectUser: PropTypes.func.isRequired,
    onSignOut: PropTypes.func,
    
    showManageAdmins: PropTypes.bool.isRequired,
    setShowManageAdmins: PropTypes.func.isRequired,
    adminsList: PropTypes.arrayOf(PropTypes.string).isRequired,
    newAdminEmail: PropTypes.string.isRequired,
    setNewAdminEmail: PropTypes.func.isRequired,
    handleAddAdmin: PropTypes.func.isRequired,
    handleRemoveAdmin: PropTypes.func.isRequired,
    saneEmail: PropTypes.string
};

export default memo(AdminUserSelectorView);
